import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Calendar, Clock, ArrowRight, Search, TrendingUp } from 'lucide-react';
import { articles } from '../content/articles';


const Resources = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(articles.map(post => post.category))];

  const filteredPosts = articles.filter(post => {
    const matchesSearch = post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         post.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()));
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#002029] via-[#00303D] to-[#004052] text-white py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              Resources & Insights
            </h1>
            <p className="text-xl text-gray-300 mb-8">
              Expert guidance on financial management, operational excellence, and growth strategies
            </p>
            
            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search articles, topics, or tags..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-12 pr-4 py-6 text-lg bg-white text-gray-900"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="py-8 bg-gray-50 border-b border-gray-200">
        <div className="container mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((category) => (
              <Button
                key={category}
                variant={selectedCategory === category ? 'default' : 'outline'}
                onClick={() => setSelectedCategory(category)}
                className={selectedCategory === category 
                  ? 'bg-[#00607A] hover:bg-[#005066] text-white' 
                  : 'border-gray-300 text-gray-700 hover:border-[#00607A] hover:text-[#00607A]'
                }
              >
                {category}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="py-16">
        <div className="container mx-auto px-6">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-xl text-gray-600">No articles found matching your search.</p>
            </div>
          ) : (
            <>
              {/* Featured Post */}
              {searchTerm === '' && selectedCategory === 'All' && filteredPosts.length > 0 && (
                <div className="mb-16">
                  <h2 className="text-2xl font-bold text-[#002029] mb-6">Featured Article</h2>
                  <Card className="border-2 border-[#00607A] hover:shadow-2xl transition-all duration-300 overflow-hidden">
                    <div className="md:flex">
                      <div className="md:w-2/5 bg-gradient-to-br from-[#002029] to-[#004052] p-12 flex items-center justify-center">
                        <TrendingUp className="h-32 w-32 text-white opacity-50" />
                      </div>
                      <div className="md:w-3/5 p-8">
                        <div className="flex items-center gap-3 mb-4">
                          <Badge className="bg-[#00607A] text-white border-none">
                            {filteredPosts[0].category}
                          </Badge>
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="h-4 w-4 mr-1" />
                            {formatDate(filteredPosts[0].date)}
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="h-4 w-4 mr-1" />
                            {filteredPosts[0].readTime}
                          </div>
                        </div>
                        <h3 className="text-3xl font-bold text-[#002029] mb-4">
                          {filteredPosts[0].title}
                        </h3>
                        <p className="text-gray-700 mb-6 leading-relaxed text-lg">
                          {filteredPosts[0].excerpt}
                        </p>
                        <div className="flex flex-wrap gap-2 mb-6">
                          {filteredPosts[0].tags.map((tag, idx) => (
                            <Badge key={idx} variant="outline" className="border-[#004052] text-[#004052]">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                        <a
  href={`/resources/${filteredPosts[0].slug}`}
  target="_blank"
  rel="noopener noreferrer"
>
  <Button className="bg-[#00607A] hover:bg-[#005066] text-white">
    Read Article
    <ArrowRight className="ml-2 h-4 w-4" />
  </Button>
</a>

                      </div>
                    </div>
                  </Card>
                </div>
              )}

              {/* Rest of Posts */}
              <h2 className="text-2xl font-bold text-[#002029] mb-6">
                {searchTerm || selectedCategory !== 'All' ? 'Search Results' : 'Latest Articles'}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.slice(searchTerm === '' && selectedCategory === 'All' ? 1 : 0).map((post) => (
                  <Card key={post.slug} className="border-2 border-gray-200 hover:border-[#00607A] hover:shadow-xl transition-all duration-300 flex flex-col">
                    <CardHeader>
                      <div className="flex items-center justify-between mb-3">
                        <Badge className="bg-[#004052] text-white border-none">
                          {post.category}
                        </Badge>
                        <div className="flex items-center text-sm text-gray-600">
                          <Clock className="h-4 w-4 mr-1" />
                          {post.readTime}
                        </div>
                      </div>
                      <CardTitle className="text-xl text-[#002029] mb-2 line-clamp-2">
                        {post.title}
                      </CardTitle>
                      <div className="flex items-center text-sm text-gray-600">
                        <Calendar className="h-4 w-4 mr-1" />
                        {formatDate(post.date)}
                      </div>
                    </CardHeader>
                    <CardContent className="flex-grow flex flex-col">
                      <CardDescription className="text-gray-700 mb-4 leading-relaxed flex-grow">
                        {post.excerpt}
                      </CardDescription>
                      <div className="flex flex-wrap gap-2 mb-4">
                        {post.tags.slice(0, 3).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="border-[#004052] text-[#004052] text-xs">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                      <a
  href={`/resources/${post.slug}`}
  target="_blank"
  rel="noopener noreferrer"
>
  <Button
    variant="outline"
    className="w-full border-[#00607A] text-[#00607A] hover:bg-[#00607A] hover:text-white transition-colors duration-300"
  >
    Read More
    <ArrowRight className="ml-2 h-4 w-4" />
  </Button>
</a>

                    </CardContent>
                  </Card>
                ))}
              </div>
            </>
          )}
        </div>
      </section>

      {/* Newsletter CTA */}
      <section className="py-16 bg-gradient-to-br from-[#002029] to-[#004052] text-white">
        <div className="container mx-auto px-6">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-4">Stay Updated</h2>
            <p className="text-xl text-gray-300 mb-8">
              Get the latest insights on financial management and operational excellence delivered to your inbox.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                className="flex-grow bg-white text-gray-900 py-6"
              />
              <Button className="bg-[#00607A] hover:bg-[#005066] text-white px-8 py-6 whitespace-nowrap">
                Subscribe
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Resources;